import * as THREE from 'three';
import { Logger } from '../core/Logger';
import { EventManager } from '../core/EventManager';
import { AdvancedPOICamera } from '../camera/AdvancedPOICamera';

/**
 * AR Manager for WebXR integration with the 3D Engine
 * Enhanced to properly handle AdvancedPOICamera during AR sessions
 */
export class ARManager {
    private renderer: THREE.WebGLRenderer | null = null;
    private scene: THREE.Scene | null = null;
    private camera: THREE.Camera | null = null;
    private advancedPOICamera: AdvancedPOICamera | null = null;
    private eventManager: EventManager;
    
    private isARSupported: boolean = false;
    private isARActive: boolean = false;
    private arSession: XRSession | null = null;
    
    // Store original state for restoration
    private originalCameraState: {
        position: THREE.Vector3;
        rotation: THREE.Euler;
        matrix: THREE.Matrix4;
    } | null = null;
    
    // Store POI camera state for restoration
    private wasAdvancedCameraActive: boolean = false;

    constructor(eventManager: EventManager) {
        this.eventManager = eventManager;
        this.checkARSupport();
    }

    /**
     * Initialize AR manager with renderer and scene from the engine
     */
    public initialize(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, advancedPOICamera?: AdvancedPOICamera): void {
        this.renderer = renderer;
        this.scene = scene;
        this.camera = camera;
        this.advancedPOICamera = advancedPOICamera || null;
        
        // Enable XR on the renderer
        this.renderer.xr.enabled = true;
        
        //Logger.Instance.Info('🥽 AR Manager initialized with engine renderer', 'AR');
        if (this.advancedPOICamera) {
            //Logger.Instance.Info('🎥 AdvancedPOICamera integration enabled', 'AR');
        }
        
        // Re-check AR support now that we have a renderer
        this.checkARSupport();
    }

    /**
     * Check if AR is supported on this device/browser
     */
    private async checkARSupport(): Promise<void> {
        // Embedded frames have no xr-spatial-tracking permission (iframe allow attr) —
        // probing WebXR there only logs a permissions-policy violation.
        try { if (new URLSearchParams(window.location.search).get('embed') === '1') return; } catch {}

        try {
            if (!navigator.xr) {
                //Logger.Instance.Info('❌ WebXR not available - AR not supported', 'AR');
                this.isARSupported = false;
                return;
            }

            const supported = await navigator.xr.isSessionSupported('immersive-ar');
            this.isARSupported = supported;
            
            if (supported) {
                //Logger.Instance.Info('✅ AR is supported on this device', 'AR');
            } else {
                //Logger.Instance.Info('❌ AR is not supported on this device', 'AR');
            }
            
            // Emit event for UI updates
            this.eventManager.emit('ar:support-checked', { supported: this.isARSupported });
            
        } catch (err) {
            Logger.Instance.Warn('⚠️ Error checking AR support:' + String(err), 'AR');
            this.isARSupported = false;
        }
    }

    /**
     * Store current camera state before entering AR
     */
    private storeCameraState(): void {
        if (!this.camera) return;
        
        this.originalCameraState = {
            position: this.camera.position.clone(),
            rotation: this.camera.rotation.clone(),
            matrix: this.camera.matrix.clone()
        };
        
        // Store AdvancedPOICamera state - we'll disable it during AR
        this.wasAdvancedCameraActive = this.advancedPOICamera !== null;
        
        //Logger.Instance.Info('📸 Camera state stored for AR session', 'AR');
        if (this.wasAdvancedCameraActive) {
            //Logger.Instance.Info('🎥 AdvancedPOICamera will be disabled during AR session', 'AR');
        }
    }

    /**
     * Restore camera state after exiting AR
     */
    private restoreCameraState(): void {
        if (!this.camera || !this.originalCameraState) return;
        
        this.camera.position.copy(this.originalCameraState.position);
        this.camera.rotation.copy(this.originalCameraState.rotation);
        this.camera.matrix.copy(this.originalCameraState.matrix);
        
        // Update projection matrix if this is a perspective camera
        if (this.camera instanceof THREE.PerspectiveCamera) {
            this.camera.updateProjectionMatrix();
        }
        
        //Logger.Instance.Info('📸 Camera state restored from AR session', 'AR');
        
        // Restore AdvancedPOICamera if it was active before AR
        if (this.wasAdvancedCameraActive && this.advancedPOICamera) {
            //Logger.Instance.Info('🎥 AdvancedPOICamera restored after AR session', 'AR');
        }
        
        this.originalCameraState = null;
        this.wasAdvancedCameraActive = false;
    }

    /**
     * Enter AR mode
     */
    public async enterAR(): Promise<boolean> {
        if (!this.isARSupported || !navigator.xr || !this.renderer) {
            Logger.Instance.Error('❌ Cannot enter AR - not supported or renderer not ready', 'AR');
            return false;
        }

        try {
            //Logger.Instance.Info('🚀 Requesting AR session...', 'AR');
            
            // Store current camera state
            this.storeCameraState();

            // Request AR session with required features
            const session = await navigator.xr.requestSession('immersive-ar', {
                requiredFeatures: ['local'],
                optionalFeatures: ['dom-overlay', 'hit-test', 'anchors']
            });

            // Set up the XR session on the renderer
            await this.renderer.xr.setSession(session);

            // Set up session event handlers
            session.addEventListener('end', () => {
                //Logger.Instance.Info('🔚 AR session ended', 'AR');
                this.handleSessionEnd();
            });

            session.addEventListener('visibilitychange', (event: any) => {
                //Logger.Instance.Info(`👁️ AR session visibility changed: ${event.session.visibilityState}`, 'AR');
            });

            // Store session reference
            this.arSession = session;
            this.isARActive = true;

            // Emit events
            this.eventManager.emit('ar:session-started', { session });
            
            //Logger.Instance.Info('✅ AR session started successfully', 'AR');
            return true;

        } catch (err: any) {
            Logger.Instance.Error(`❌ Failed to enter AR mode: ${err.message}`, 'AR');
            this.restoreCameraState();
            this.eventManager.emit('ar:error', { error: err.message });
            return false;
        }
    }

    /**
     * Exit AR mode
     */
    public async exitAR(): Promise<void> {
        if (!this.arSession) {
            //Logger.Instance.Info('ℹ️ No active AR session to exit', 'AR');
            return;
        }

        try {
            //Logger.Instance.Info('🛑 Ending AR session...', 'AR');
            await this.arSession.end();
            // Session end event handler will clean up state
        } catch (err: any) {
            Logger.Instance.Error(`❌ Error ending AR session: ${err.message}`, 'AR');
            // Force cleanup even if end() fails
            this.handleSessionEnd();
        }
    }

    /**
     * Toggle AR mode on/off
     */
    public async toggleAR(): Promise<void> {
        if (this.isARActive) {
            await this.exitAR();
        } else {
            await this.enterAR();
        }
    }

    /**
     * Handle AR session end (cleanup)
     */
    private handleSessionEnd(): void {
        this.arSession = null;
        this.isARActive = false;
        
        // Restore camera state
        this.restoreCameraState();
        
        // Clear XR session from renderer
        if (this.renderer) {
            this.renderer.xr.setSession(null);
        }
        
        // Emit event
        this.eventManager.emit('ar:session-ended');
        
        //Logger.Instance.Info('🔚 AR session cleanup complete', 'AR');
    }

    /**
     * Update method called by the engine
     */
    public update(deltaTime: number): void {
        // During AR session, we DON'T update the AdvancedPOICamera
        // The WebXR system handles camera positioning based on device movement
        
        // Only update AdvancedPOICamera when NOT in AR mode
        if (!this.isARActive && this.advancedPOICamera) {
            // Let the AdvancedPOICamera handle its own updates when not in AR
            // This happens automatically in the EngineDemoApp.onUpdate() method
        }
        
        // AR-specific update logic if needed
        // Most AR functionality is handled by Three.js XR system
    }

    /**
     * Get AR status for UI
     */
    public getStatus(): {
        isSupported: boolean;
        isActive: boolean;
        session: XRSession | null;
        cameraManagement: string;
    } {
        let cameraManagement = 'AdvancedPOICamera';
        if (this.isARActive) {
            cameraManagement = 'WebXR Device Camera';
        }
        
        return {
            isSupported: this.isARSupported,
            isActive: this.isARActive,
            session: this.arSession,
            cameraManagement: cameraManagement
        };
    }

    /**
     * Dispose of AR resources
     */
    public dispose(): void {
        if (this.arSession) {
            this.arSession.end().catch(err => {
                Logger.Instance.Warn('⚠️ Error disposing AR session:' + String(err), 'AR');
            });
        }
        
        //Logger.Instance.Info('🧹 AR Manager disposed', 'AR');
    }
} 